<template>
  <section class="bg-background-light py-20 lg:py-28" :id="id">
    <div class="mx-auto max-w-[1200px] px-6 lg:px-10">
      <div class="relative overflow-hidden rounded-[2rem] border border-primary/20 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent px-6 py-12 sm:px-8 lg:px-16 lg:py-16">
        <div class="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full bg-primary/20 blur-3xl" aria-hidden="true"></div>

        <div class="relative grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          <!-- Pitch -->
          <div>
            <h2 class="text-4xl font-black leading-[1.05] tracking-[-0.02em] text-text-main sm:text-5xl">
              {{ t('ai_scan.title') }}
            </h2>
            <p class="mt-4 max-w-[520px] text-lg text-text-muted">{{ t('ai_scan.subtitle') }}</p>

            <ul class="mt-8 flex flex-col gap-3">
              <li v-for="benefit in benefits" :key="benefit" class="flex items-start gap-3 text-text-main">
                <span class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-text-main">
                  <span class="material-symbols-outlined text-[16px]">check</span>
                </span>
                <span class="font-medium">{{ t(benefit) }}</span>
              </li>
            </ul>
          </div>

          <!-- Form -->
          <div class="rounded-3xl border border-text-main/10 bg-white p-6 sm:p-8">
            <div v-if="success" class="flex h-full min-h-[320px] flex-col items-center justify-center text-center">
              <span class="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-text-main">
                <span class="material-symbols-outlined text-[28px]">mark_email_read</span>
              </span>
              <h3 class="mt-6 text-2xl font-black tracking-tight text-text-main">{{ t('ai_scan.form.success_title') }}</h3>
              <p class="mt-3 max-w-[360px] text-text-muted">{{ t('ai_scan.form.success_text') }}</p>
            </div>

            <!-- No `novalidate`: the browser's own required-field checks are the
                 cheapest first line of defence before we hit the API. -->
            <form v-else class="flex flex-col gap-5" @submit.prevent="submit">
              <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <label class="flex flex-col gap-2">
                  <span class="text-sm font-bold text-text-main">{{ t('ai_scan.form.name.label') }}</span>
                  <input
                    v-model.trim="form.name"
                    type="text"
                    name="name"
                    autocomplete="name"
                    required
                    :placeholder="t('ai_scan.form.name.placeholder')"
                    class="h-12 w-full rounded-lg border border-text-main/15 bg-white px-4 text-base text-text-main transition-colors placeholder:text-text-muted/60 focus:border-primary focus:outline-none focus:ring-0"
                  />
                </label>

                <label class="flex flex-col gap-2">
                  <span class="text-sm font-bold text-text-main">{{ t('ai_scan.form.company.label') }}</span>
                  <input
                    v-model.trim="form.company"
                    type="text"
                    name="organization"
                    autocomplete="organization"
                    required
                    :placeholder="t('ai_scan.form.company.placeholder')"
                    class="h-12 w-full rounded-lg border border-text-main/15 bg-white px-4 text-base text-text-main transition-colors placeholder:text-text-muted/60 focus:border-primary focus:outline-none focus:ring-0"
                  />
                </label>
              </div>

              <label class="flex flex-col gap-2">
                <span class="text-sm font-bold text-text-main">{{ t('ai_scan.form.email.label') }}</span>
                <input
                  v-model.trim="form.email"
                  type="email"
                  name="email"
                  autocomplete="email"
                  required
                  :placeholder="t('ai_scan.form.email.placeholder')"
                  class="h-12 w-full rounded-lg border border-text-main/15 bg-white px-4 text-base text-text-main transition-colors placeholder:text-text-muted/60 focus:border-primary focus:outline-none focus:ring-0"
                />
              </label>

              <label class="flex flex-col gap-2">
                <span class="text-sm font-bold text-text-main">{{ t('ai_scan.form.description.label') }}</span>
                <textarea
                  v-model.trim="form.description"
                  name="description"
                  rows="4"
                  required
                  :placeholder="t('ai_scan.form.description.placeholder')"
                  class="w-full resize-none rounded-lg border border-text-main/15 bg-white p-4 text-base text-text-main transition-colors placeholder:text-text-muted/60 focus:border-primary focus:outline-none focus:ring-0"
                ></textarea>
              </label>

              <!-- Honeypot: hidden from humans, bots fill it in and get rejected. -->
              <div class="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
                <label>
                  Website
                  <input v-model="form.website" type="text" name="website" tabindex="-1" autocomplete="off" />
                </label>
              </div>

              <button
                type="submit"
                :disabled="submitting"
                class="inline-flex h-13 items-center justify-center gap-2 rounded-xl bg-primary px-7 text-base font-bold text-text-main shadow-[0_8px_30px_-8px_rgba(255,174,0,0.7)] transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {{ submitting ? t('ai_scan.form.submitting') : t('ai_scan.form.submit') }}
                <span v-if="!submitting" class="material-symbols-outlined text-[20px]">arrow_forward</span>
              </button>

              <p v-if="error" class="text-sm font-medium text-red-600" role="alert">{{ error }}</p>

              <p class="text-xs text-text-muted">{{ t('ai_scan.form.disclaimer') }}</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

defineProps<{ id?: string }>();

const { t, locale } = useI18n();

const benefits = ['ai_scan.benefit_1', 'ai_scan.benefit_2', 'ai_scan.benefit_3'];

const submitting = ref(false);
const success = ref(false);
const error = ref<string | null>(null);

const form = reactive({
  name: '',
  email: '',
  company: '',
  description: '',
  website: '', // honeypot, stays empty
});

const submit = async () => {
  error.value = null;
  submitting.value = true;

  try {
    const res = await fetch('/api/ai-scan', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ ...form, locale: locale.value }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => null);

      // Laravel validation errors arrive as { errors: { field: [...] } }
      if (data?.errors) {
        const firstKey = Object.keys(data.errors)[0];
        error.value = data.errors[firstKey]?.[0] ?? t('ai_scan.form.error');
      } else {
        error.value = data?.message ?? t('ai_scan.form.error');
      }

      return;
    }

    success.value = true;
  } catch (e) {
    error.value = t('ai_scan.form.error');
  } finally {
    submitting.value = false;
  }
};
</script>

<style scoped>
.h-13 { height: 3.25rem; }
</style>
